import React, { useState, useMemo, useEffect } from "react";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import { useFetchDetail, useFetchDataDetail } from "@hooks/useFetchDetail";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { useAddEditGoodReceipt } from "../hooks/useAddEditGoodReceipt";
import GoodsReceiptsFormCst from "../GoodsReceiptsFormCst";
import dayjs from "dayjs";
import { useAuth } from "@context/auth";

const EditGoodReceipt: React.FC<{
  accounting?: boolean;
}> = ({ accounting = false }) => {
  const { idProject, id, idCostModel, idCostModelDetail, idGoodReceipt } =
    useParams();
  const { t } = useTranslation(["goods-receipts-page"]);
  const navigate = useNavigate();
  const [openOutcome, setOpenOutcome] = useState(false);
  const { user } = useAuth();

  /*Permission to indetify PM*/
  const isPM = user?.permissions.find(
    (permission: any) =>
      permission === "usermgmt.budget.project.mark.as.pending.activation",
  );

  const {
    columns,
    dataDetail: dataGoodReceipt,
    mutate,
  } = useFetchDataDetail(
    `${ServicesURL.getGoodsReceiptsByCost.replaceAll(":costId", String(idCostModelDetail))}/${idGoodReceipt}?corporateId=${id}`,
    PagesID["projects.cost-goods-receipts"],
  );

  /*Get the project information*/
  const { dataDetail: dataProject, mutate: projectMutate } = useFetchDataDetail(
    `${ServicesURL.getProjects}/${idProject}?corporateId=${id}`,
    PagesID["projects"],
  );

  /*Get the cost model information*/
  const { dataDetail: dataCostModel, mutate: costModelMutate } =
    useFetchDataDetail(
      `${ServicesURL.postCostModel.replace(":budgetProjectId", String(idProject))}/${idCostModel}?corporateId=${id}`,
      PagesID["projects.cost-model"],
    );

  /*Get the cost detail information*/
  const { dataDetail: dataCostDetail, mutate: costMutate } = useFetchDataDetail(
    `${ServicesURL.getCostModelDetail}/${idCostModelDetail}?corporateId=${id}`,
    PagesID["projects.cost-detail"],
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetGoodReceiptAssociations,
    setCustomerAssociation,
    customerAssociation,
    openAssociations,
  } = useAddEditGoodReceipt(dataGoodReceipt || []);

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("edit.outcome.good-receipt.messageLoading"),
        title: t("edit.outcome.good-receipt.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("edit.outcome.good-receipt.messageLoading"),
      title: t("edit.outcome.good-receipt.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerGoodReceiptDetail } = useSWRMutation(
    `${ServicesURL.postGoodsReceipts.replaceAll(":costId", String(idCostModelDetail))}/${idGoodReceipt}?corporateId=${id}`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  /*Submit of the form for create a new cost model*/
  const onSubmit = async (inputForm: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));

    const objToSend: any = {
      value: inputForm.value || null,
      notes: inputForm.notes || null,
      status: inputForm.status || null,
      funded: inputForm.funded || false,
      customer_id:
        customerAssociation && customerAssociation.length > 0
          ? parseInt(customerAssociation[0].id)
          : null,
      sap_code: inputForm.sap_code || null,
      sap_value: inputForm.sap_value || null,
      sap_date: !!inputForm.sap_date
        ? dayjs(new Date(inputForm.sap_date)).format("YYYY-MM-DD")
        : null,
    };

    try {
      await triggerGoodReceiptDetail(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("edit.outcome.good-receipt.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }
    mutate(inputForm);
    costMutate(inputForm);
    costModelMutate(inputForm);
    projectMutate(inputForm);
  };

  /*Building the data to send at the form with project and cost info*/
  const formField = useMemo(() => {
    if (
      dataGoodReceipt != null &&
      Object.keys(dataGoodReceipt).length > 0 &&
      dataCostModel.dto != null &&
      dataProject.dto != null &&
      dataCostDetail != null &&
      Object.keys(dataCostDetail).length > 0
    ) {
      return {
        ...dataGoodReceipt,
        info_detail_cost: dataCostDetail || null,
        info_project: dataProject?.dto || null,
        info_cost_model: dataCostModel?.dto || null,
      };
    } else {
      return null;
    }
  }, [dataCostModel, dataProject, dataCostDetail, dataGoodReceipt]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-cost-centers.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("edit.hero-title")} description={""} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <GoodsReceiptsFormCst
              row={formField}
              columns={columns}
              onSubmit={onSubmit}
              backButton={() => {
                accounting
                  ? navigate(
                      `/corporate/${id}/detail/budget-management/accounting?tab=GR`,
                    )
                  : navigate(-1);
              }}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              handleDelete={(accessorKey, value) =>
                handleDelete(accessorKey, value)
              }
              getValues={(accessorKey) => getValues(accessorKey)}
              association={openAssociations}
              fullWidth={true}
              customerAssociation={customerAssociation}
              setCustomerAssociation={setCustomerAssociation}
              resetGoodReceiptAssociations={resetGoodReceiptAssociations}
              isAccounting={accounting}
              readOnly={
                !!isPM &&
                (formField?.status != "REQUESTED" ||
                  dataProject?.dto?.status === "CLOSED")
              }
            />
          </Box>
        )}
      </Container>
      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            setOpenOutcome(!openOutcome);
            navigate(
              accounting
                ? `/corporate/${id}/detail/budget-management/accounting?tab=GR`
                : `/corporate/${id}/detail/budget-management/projects/edit/${idProject}/cost-model/${idCostModel}/cost/${idCostModelDetail}/goods-receipts`,
            );
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditGoodReceipt;
